import type {
    Extension,
    ValidatorMap,
    ExtensionAPI,
    DalItem,
    DalValue,
    DmApis,
    ValidatorResult,
    InitializeExtArgs
} from '@wix/document-manager-core'
import type {
    CompRef,
    BoundObjectRef,
    BindingsObject,
    BindingsMap,
    TemplateBindingsMap,
    BoundPropertiesPerProvider,
    FunctionBindingsMap,
    TemplateListItem,
    Pointer,
    FunctionBindingConfig,
    BoundObjectOrInnerElement,
    ListContextBoundObjectsOptions,
    InnerElementPath,
    BindingArgument
} from '@wix/document-services-types'
import {
    BindingType,
    BindingsConfigType,
    BindingItemType,
    TemplateBindingType,
    ArgumentType
} from '../../constants/dsTypesConstants'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {StructureExtensionAPI} from '../structure'
import type {HooksExtensionApi} from '../hooks/hooks'
import type {RelationshipsAPI} from '../relationships'
import {ReportableError} from '@wix/document-manager-utils'
import {DATA_TYPES} from '../../constants/constants'
import {CONTEXTS_HOOKS, type ContextDetachAfterEvent} from '../contexts/hooks'
import _ from 'lodash'
import type {ContextsExtensionAPIInternal} from '../contexts/contexts'
import type {SlotsExtensionAPI} from '../slots/slots'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import {
    walkSubtree,
    getOwningComponentForContextConfig,
    getInnerElementPathAndOwner,
    type TreeWalkVisitor
} from './utils'
import {componentHasContextType, createBindingsValidators, hasBindingConfigMatching} from './bindingsUtils'
import {stripHashIfExists} from '../../utils/refArrayUtils'

const BINDINGS_NAMESPACE = DATA_TYPES.bindings

type BindingsExtensionAPI = ExtensionAPI & {
    bindings: BindingsObject
}

const throwBindingsError = (method: string, message: string): never => {
    throw new ReportableError({errorType: 'BindingsError', message: `bindings.${method}: ${message}`})
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, pointers, dal}: DmApis): BindingsExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI & StructureExtensionAPI
        const {relationships} = extensionAPI as RelationshipsAPI
        const {validateCompRef, validateComponentExists} = createBindingsValidators({
            errorType: 'BindingsError',
            apiName: 'bindings',
            dataModel
        })

        const validateContextType = (method: string, contextType: string): void => {
            if (!contextType) {
                throwBindingsError(
                    method,
                    'contextType is required. Pass the name of the context you want to attach to the component'
                )
            }
            if (!_.isString(contextType)) {
                throwBindingsError(
                    method,
                    'contextType must be a string. Pass the name of the context you want to attach to the component'
                )
            }
        }

        // TODO: This function is temporary, to provide backward compatibility
        const normalizeBoundObjectRef = (boundRef: CompRef | BoundObjectRef): BoundObjectRef => {
            if ((boundRef as any)?.compRef) {
                return boundRef as BoundObjectRef
            }
            return {compRef: boundRef as CompRef}
        }

        const getBoundObjectPointer = (boundRef: BoundObjectRef): Pointer => {
            const contextsAPI = (extensionAPI as ContextsExtensionAPIInternal).contexts
            return boundRef.contextType ? contextsAPI.getContextPointer(boundRef) : (boundRef.compRef as Pointer)
        }

        const getBindingsListFromBoundObject = (boundObjectPointer: Pointer): DalItem | undefined => {
            return dataModel.components.getItem(boundObjectPointer, BINDINGS_NAMESPACE)
        }

        const bind = (
            boundRef: CompRef | BoundObjectRef,
            contextType: string,
            bindingsMap: BindingsMap,
            replace: boolean
        ): void => {
            boundRef = normalizeBoundObjectRef(boundRef)

            validateCompRef('bind', boundRef?.compRef)
            validateContextType('bind', contextType)
            validateComponentExists('bind', boundRef.compRef)

            const boundObjectPointer = getBoundObjectPointer(boundRef)
            const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)

            if (!existingCompBindingsList) {
                dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                    type: 'BindingsList',
                    data: [
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingCompBindingsList?.data ?? []

            const configAlreadyDefined = _.find(
                bindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!configAlreadyDefined) {
                dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                    ...existingCompBindingsList,
                    data: [
                        ...bindingConfigs,
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const updatedConfig = {
                ...configAlreadyDefined,
                properties: replace ? bindingsMap : {...configAlreadyDefined.properties, ...bindingsMap}
            }

            const updatedBindingConfigs = bindingConfigs.map((config: DalItem) =>
                config.id === updatedConfig.id ? updatedConfig : config
            )

            dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                ...existingCompBindingsList,
                data: updatedBindingConfigs
            })
        }

        const validatePropertyName = (method: string, propertyName: string): void => {
            if (!propertyName) {
                throwBindingsError(method, 'propertyName is required')
            }
            if (!_.isString(propertyName)) {
                throwBindingsError(method, 'propertyName must be a string')
            }
        }

        const bindFunction = (
            boundRef: CompRef | BoundObjectRef,
            functionLibraryType: string,
            functionBindingsMap: FunctionBindingsMap
        ): void => {
            boundRef = normalizeBoundObjectRef(boundRef)

            validateCompRef('functions.bind', boundRef?.compRef)
            validateContextType('functions.bind', functionLibraryType)
            validateComponentExists('functions.bind', boundRef.compRef)

            const boundObjectPointer = getBoundObjectPointer(boundRef)

            for (const [propertyName, binding] of Object.entries(functionBindingsMap)) {
                validatePropertyName('functions.bind', propertyName)

                const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)
                const newConfig = {
                    type: 'FunctionBindingConfig',
                    propertyName,
                    functionBindingItem: {
                        type: ArgumentType.FunctionBinding,
                        functionLibraryType,
                        function: binding.function,
                        ...binding.callOptions
                    }
                }

                if (!existingCompBindingsList) {
                    dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                        type: 'BindingsList',
                        data: [newConfig]
                    })
                    continue
                }

                const bindingConfigs = existingCompBindingsList?.data ?? []

                dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                    ...existingCompBindingsList,
                    data: [...bindingConfigs, newConfig]
                })
            }
        }

        const bindTemplate = (
            boundRef: CompRef | BoundObjectRef,
            templateComponentId: string,
            bindingsMap: TemplateBindingsMap
        ): void => {
            boundRef = normalizeBoundObjectRef(boundRef)
            validateCompRef('templates.bind', boundRef?.compRef)
            validateComponentExists('templates.bind', boundRef.compRef)

            if (!templateComponentId) {
                throwBindingsError('templates.bind', 'templateComponentId is required')
            }

            if (!bindingsMap || _.isEmpty(bindingsMap)) {
                throwBindingsError('templates.bind', 'properties is required and must not be empty')
            }

            const boundObjectPointer = getBoundObjectPointer(boundRef)
            const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)

            if (!existingCompBindingsList) {
                dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                    type: 'BindingsList',
                    data: [
                        {
                            type: 'TemplateBindingConfig',
                            templateComponentId,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingCompBindingsList?.data ?? []
            const existingIndex = _.findIndex(bindingConfigs, {
                type: 'TemplateBindingConfig',
                templateComponentId
            })

            const newConfig = {
                type: 'TemplateBindingConfig',
                templateComponentId,
                properties:
                    existingIndex >= 0 ? {...bindingConfigs[existingIndex].properties, ...bindingsMap} : bindingsMap
            }

            const updatedConfigs =
                existingIndex >= 0
                    ? bindingConfigs.map((c: DalItem, i: number) => (i === existingIndex ? newConfig : c))
                    : [...bindingConfigs, newConfig]

            dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                ...existingCompBindingsList,
                data: updatedConfigs
            })
        }

        const unbindProperties = (boundRef: CompRef | BoundObjectRef, properties: string[]): void => {
            boundRef = normalizeBoundObjectRef(boundRef)

            const {compRef} = boundRef

            validateCompRef('unbindProperties', compRef as CompRef)
            validateComponentExists('unbindProperties', compRef as CompRef)

            const boundObjectPointer = getBoundObjectPointer(boundRef)

            const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)

            if (!existingCompBindingsList) {
                throwBindingsError('unbindProperties', `This component does not have bindings, id:${compRef.id}`)
            }

            const existingContextBindingConfigs = existingCompBindingsList?.data ?? []
            const configsToRemove = new Set<DalItem>()
            const configUpdates = new Map<DalItem, DalItem>()

            for (const config of existingContextBindingConfigs) {
                if (config.type === 'FunctionBindingConfig') {
                    if (properties.includes(config.propertyName)) {
                        configsToRemove.add(config)
                    }
                } else if (config.type === 'ContextBindingConfig' || config.type === 'TemplateBindingConfig') {
                    const shouldUpdateConfig = _.some(Object.keys(config.properties), (key: string) =>
                        properties.includes(key)
                    )
                    if (!shouldUpdateConfig) {
                        continue
                    }
                    const newProperties = _.omit(config.properties, properties)
                    if (_.isEmpty(newProperties)) {
                        configsToRemove.add(config)
                    } else {
                        const updatedConfig = {
                            ...config,
                            properties: newProperties
                        } as DalItem
                        configUpdates.set(config, updatedConfig)
                    }
                } else {
                    throw new Error(`unexpected binding type when calling unbindProperties: ${config.type}`)
                }
            }

            if (configsToRemove.size > 0 || configUpdates.size > 0) {
                const updatedConfigs = existingContextBindingConfigs
                    .filter((config: DalItem) => !configsToRemove.has(config))
                    .map((config: DalItem) => configUpdates.get(config) ?? config)
                if (_.isEmpty(updatedConfigs)) {
                    dataModel.components.removeItem(boundObjectPointer, BINDINGS_NAMESPACE)
                } else {
                    dataModel.components.addItem(boundObjectPointer, BINDINGS_NAMESPACE, {
                        ...existingCompBindingsList,
                        data: updatedConfigs
                    })
                }
            }
        }

        const getBoundProperties = (boundRef: CompRef | BoundObjectRef): BoundPropertiesPerProvider => {
            boundRef = normalizeBoundObjectRef(boundRef)

            const {compRef} = boundRef

            validateCompRef('getBoundProperties', compRef as CompRef)
            validateComponentExists('getBoundProperties', compRef as CompRef)

            const boundObjectPointer = getBoundObjectPointer(boundRef)

            const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)
            if (!existingCompBindingsList?.data || existingCompBindingsList.data.length === 0) {
                return {}
            }

            const bindings: BoundPropertiesPerProvider = {}

            for (const config of existingCompBindingsList.data) {
                const dalItem = config as DalItem
                if (dalItem.type === 'FunctionBindingConfig') {
                    const functionConfig = dalItem as unknown as FunctionBindingConfig & {type: string}
                    const newEntry = {
                        [functionConfig.propertyName]: {
                            function: functionConfig.functionBindingItem.function,
                            callOptions: {
                                asCallback: functionConfig.functionBindingItem.asCallback,
                                arguments: functionConfig.functionBindingItem.arguments
                            }
                        }
                    }
                    if (!bindings[functionConfig.functionBindingItem.functionLibraryType]) {
                        bindings[functionConfig.functionBindingItem.functionLibraryType] = {
                            bindingType: BindingType.FunctionLibrary,
                            functionLibraryType: functionConfig.functionBindingItem.functionLibraryType,
                            bindingsMap: newEntry
                        }
                        continue
                    }
                    bindings[functionConfig.functionBindingItem.functionLibraryType].bindingsMap[
                        functionConfig.propertyName
                    ] = newEntry[functionConfig.propertyName]
                } else if (dalItem.type === 'ContextBindingConfig') {
                    bindings[dalItem.contextProvider] = {
                        bindingType: BindingType.ContextProvider,
                        contextType: dalItem.contextProvider,
                        bindingsMap: dalItem.properties
                    }
                } else if (dalItem.type === 'TemplateBindingConfig') {
                    bindings[dalItem.templateComponentId] = {
                        bindingType: BindingType.Template,
                        templateComponentId: dalItem.templateComponentId,
                        bindingsMap: dalItem.properties
                    }
                } else {
                    throw new Error(`unexpected binding type when calling getBoundProperties: ${dalItem.type}`)
                }
            }

            return bindings
        }

        const unbind = (boundRef: CompRef | BoundObjectRef): void => {
            boundRef = normalizeBoundObjectRef(boundRef)

            const {compRef} = boundRef

            validateCompRef('unbind', compRef)
            validateComponentExists('unbind', compRef)

            const boundObjectPointer = getBoundObjectPointer(boundRef)

            const existingCompBindingsList = getBindingsListFromBoundObject(boundObjectPointer)
            if (!existingCompBindingsList) {
                return
            }

            dataModel.components.removeItem(boundObjectPointer, BINDINGS_NAMESPACE)
        }

        const listTemplates = (boundRef: CompRef | BoundObjectRef): TemplateListItem[] => {
            boundRef = normalizeBoundObjectRef(boundRef)
            const {compRef} = boundRef
            validateCompRef('templates.list', compRef)
            validateComponentExists('templates.list', compRef)

            const {slots, manifest} = extensionAPI as SlotsExtensionAPI & ManifestExtensionAPI
            const results: TemplateListItem[] = []
            const ancestors = pointers.structure.getAncestors(compRef)

            let currentChild: CompRef = compRef
            for (const ancestor of ancestors) {
                const ancestorRef = ancestor as CompRef
                const containerInfoMap = slots.getPopulatedComponentContainerInfoMap(ancestorRef)
                const containerInfo = containerInfoMap[currentChild.id] ?? containerInfoMap[`#${currentChild.id}`]

                if (containerInfo) {
                    const manifestContainers = manifest.getData(ancestorRef, false, [{prop: 'container'}])
                    const allTemplateSlots = manifestContainers.filter(
                        ({metaData}) => metaData?.containerType === 'template'
                    )
                    if (allTemplateSlots.length > 0) {
                        const componentType = dal.get(ancestorRef)?.componentType
                        const bindingsList = getBindingsListFromBoundObject(ancestorRef as Pointer)
                        const bindingConfigs: DalItem[] = bindingsList?.data ?? []

                        for (const slot of allTemplateSlots) {
                            const listItemPath = slot.metaData?.template?.dataItemPath?.itemKey ?? ''
                            const slotContainerInfo = _.find(
                                Object.values(containerInfoMap),
                                (info: any) => info.slotName === slot.prop
                            )
                            if (!slotContainerInfo) {
                                continue
                            }
                            const templateRef = {
                                templateComponentId: (slotContainerInfo as any).compRef.id
                            }

                            const contextConfig = _.find(
                                bindingConfigs,
                                (config: DalItem) =>
                                    config.type === 'ContextBindingConfig' && config.properties?.[listItemPath]
                            ) as unknown as DalItem | undefined
                            const functionConfig = _.find(bindingConfigs, {
                                type: 'FunctionBindingConfig',
                                propertyName: listItemPath
                            }) as unknown as DalItem | undefined
                            const templateConfig = _.find(
                                bindingConfigs,
                                (config: DalItem) =>
                                    config.type === 'TemplateBindingConfig' && config.properties?.[listItemPath]
                            ) as unknown as DalItem | undefined

                            if (contextConfig) {
                                results.push({
                                    type: 'TemplateListItemContext',
                                    templateComponentId: templateRef.templateComponentId,
                                    componentType,
                                    contextType: contextConfig.contextProvider,
                                    boundContext: contextConfig.properties[listItemPath]
                                })
                            } else if (functionConfig) {
                                results.push({
                                    type: 'TemplateListItemFunction',
                                    templateComponentId: templateRef.templateComponentId,
                                    componentType,
                                    boundFunction: functionConfig.functionBindingItem
                                })
                            } else if (templateConfig) {
                                results.push({
                                    type: 'TemplateListItemTemplate',
                                    templateComponentId: templateRef.templateComponentId,
                                    componentType,
                                    boundTemplateComponentId: templateConfig.templateComponentId,
                                    boundTemplate: templateConfig.properties[listItemPath]
                                })
                            } else {
                                results.push({
                                    type: 'TemplateListItemNotBound',
                                    templateComponentId: templateRef.templateComponentId,
                                    componentType
                                })
                            }
                        }
                    }
                }
                currentChild = ancestorRef
            }
            return results
        }

        const collectBoundObjectsFromComponent = (
            compRef: CompRef,
            predicate: (config: DalItem) => boolean,
            results: BoundObjectOrInnerElement[]
        ): void => {
            const compBindings = getBindingsListFromBoundObject(compRef as Pointer)
            if (hasBindingConfigMatching(compBindings, predicate)) {
                results.push({type: 'BoundObject', boundObjectRef: {compRef}})
            }
            const contextList = dataModel.components.getItem(compRef, DATA_TYPES.contexts)
            if (contextList?.values?.length) {
                for (const contextConfig of contextList.values) {
                    const contextPointer = pointers.getPointer(contextConfig.id!, DATA_TYPES.contexts)
                    const contextBindings = getBindingsListFromBoundObject(contextPointer)
                    if (hasBindingConfigMatching(contextBindings, predicate)) {
                        results.push({
                            type: 'BoundObject',
                            boundObjectRef: {compRef, contextType: contextConfig.contextType}
                        })
                    }
                }
            }
        }

        const collectBoundObjectsFromInnerElement = (
            compRef: CompRef,
            path: InnerElementPath,
            innerElementId: string,
            predicate: (config: DalItem) => boolean,
            results: BoundObjectOrInnerElement[]
        ): void => {
            const innerElementPointer = {id: innerElementId, type: DATA_TYPES.innerElements}
            const innerBindings = getBindingsListFromBoundObject(innerElementPointer)
            if (hasBindingConfigMatching(innerBindings, predicate)) {
                results.push({type: 'InnerElement', compRef, path})
            }
        }

        const forEachMatchingOwner = (
            predicate: (config: DalItem) => boolean,
            onOwner: (ownerPointer: Pointer) => void
        ): void => {
            for (const pointer of dal.getAllItemsInNamespace(BINDINGS_NAMESPACE)) {
                const item = dal.get(pointer)
                if (!item || item.type !== 'BindingsList') continue

                const refs: string[] = item.data ?? []
                const hasMatch = refs.some(ref => {
                    const config = dal.get({id: stripHashIfExists(ref), type: BINDINGS_NAMESPACE})
                    return config && predicate(config)
                })
                if (!hasMatch) continue

                const ownerPointers = relationships.getReferencesToPointer(pointer)
                if (!ownerPointers.length) continue

                onOwner(ownerPointers[0])
            }
        }

        const resolveBindingOwner = (
            ownerPointer: Pointer
        ): {compRef: CompRef; result: BoundObjectOrInnerElement} | undefined => {
            if (ownerPointer.type === DATA_TYPES.contexts) {
                const contextConfig = dal.get(ownerPointer)
                const contextType = contextConfig?.contextType
                const compRef = getOwningComponentForContextConfig(ownerPointer, extensionAPI as RelationshipsAPI)
                if (!compRef) return undefined
                return {compRef, result: {type: 'BoundObject', boundObjectRef: {compRef, contextType}}}
            }

            if (ownerPointer.type === DATA_TYPES.innerElements) {
                const {path, compRef} = getInnerElementPathAndOwner(
                    ownerPointer.id,
                    extensionAPI as RelationshipsAPI,
                    dal
                )
                if (!compRef) return undefined
                return {compRef, result: {type: 'InnerElement', compRef, path}}
            }

            const compRef = ownerPointer as CompRef
            return {compRef, result: {type: 'BoundObject', boundObjectRef: {compRef}}}
        }

        const listBoundObjectsForContext = (
            contextType: string,
            options?: ListContextBoundObjectsOptions
        ): BoundObjectOrInnerElement[] => {
            validateContextType('contexts.listBoundObjects', contextType)

            const results: BoundObjectOrInnerElement[] = []
            const predicate = (config: DalItem) =>
                config.type === 'ContextBindingConfig' && config.contextProvider === contextType

            if (options?.restrictUnderContainer) {
                validateCompRef('contexts.listBoundObjects', options.restrictUnderContainer)
                validateComponentExists('contexts.listBoundObjects', options.restrictUnderContainer)
                const visitor: TreeWalkVisitor = {
                    onComponent: compRef => collectBoundObjectsFromComponent(compRef, predicate, results),
                    onInnerElement: (compRef, path, innerElementId) =>
                        collectBoundObjectsFromInnerElement(compRef, path, innerElementId, predicate, results),
                    shouldStopAtComponent: (compRef: CompRef) =>
                        componentHasContextType(compRef, contextType, dataModel)
                }
                walkSubtree(options.restrictUnderContainer, pointers, extensionAPI, visitor)
            } else {
                forEachMatchingOwner(predicate, ownerPointer => {
                    const resolved = resolveBindingOwner(ownerPointer)
                    if (!resolved) return
                    results.push(resolved.result)
                })
            }

            return results
        }

        const listBoundObjectsForFunction = (functionLibraryType: string): BoundObjectOrInnerElement[] => {
            validateContextType('functions.listBoundObjects', functionLibraryType)

            const predicate = (config: DalItem) =>
                config.type === 'FunctionBindingConfig' &&
                config.functionBindingItem?.functionLibraryType === functionLibraryType

            const results: BoundObjectOrInnerElement[] = []

            forEachMatchingOwner(predicate, ownerPointer => {
                const resolved = resolveBindingOwner(ownerPointer)
                if (!resolved) return
                results.push(resolved.result)
            })

            return results
        }

        return {
            bindings: {
                bind: (boundRef: CompRef | BoundObjectRef, contextType: string, bindingsMap: BindingsMap): void =>
                    bind(boundRef, contextType, bindingsMap, false),
                bindAll: (boundRef: CompRef | BoundObjectRef, contextType: string, bindingsMap: BindingsMap): void =>
                    bind(boundRef, contextType, bindingsMap, true),
                unbindProperties: (boundRef: CompRef | BoundObjectRef, properties: string[]): void =>
                    unbindProperties(boundRef, properties),
                getBoundProperties: (boundRef: CompRef | BoundObjectRef): BoundPropertiesPerProvider =>
                    getBoundProperties(boundRef),
                unbind: (boundRef: CompRef | BoundObjectRef): void => unbind(boundRef),
                contexts: {
                    listBoundObjects: listBoundObjectsForContext
                },
                functions: {
                    bind: (
                        boundRef: CompRef | BoundObjectRef,
                        functionLibraryType: string,
                        functionBindingsMap: FunctionBindingsMap
                    ): void => bindFunction(boundRef, functionLibraryType, functionBindingsMap),
                    listBoundObjects: listBoundObjectsForFunction
                },
                templates: {
                    bind: (
                        componentRef: CompRef | BoundObjectRef,
                        templateComponentId: string,
                        bindingsMap: TemplateBindingsMap
                    ): void => bindTemplate(componentRef, templateComponentId, bindingsMap),
                    list: (boundRef: CompRef | BoundObjectRef): TemplateListItem[] => listTemplates(boundRef)
                }
            }
        }
    }

    const createValidator = ({dal, extensionAPI}: DmApis): ValidatorMap => {
        const {relationships} = extensionAPI as RelationshipsAPI
        const {contexts: contextsAPI} = extensionAPI as ContextsExtensionAPIInternal
        const {components} = extensionAPI as StructureExtensionAPI
        const generateErrorDuplicatePropertyBinding = (property: string) => ({
            shouldFail: true,
            type: 'propertyBoundMoreThanOnce',
            message: `The property is bound multiple times and it should be bound only once. Call ds.bindings.getBoundProperties to see where`,
            extras: {property}
        })

        const getResolvedBoundRefFromConfigPointer = (configPointer: Pointer): BoundObjectRef => {
            const bindingsListPtr = relationships.getOwningReferencesToPointer(configPointer)[0]
            const boundObjectPtr = relationships.getOwningReferencesToPointer(bindingsListPtr)[0]
            const isContext = boundObjectPtr.type === DATA_TYPES.contexts
            const isInnerElement = boundObjectPtr.type === DATA_TYPES.innerElements
            const resolveCompRef = (): CompRef => {
                if (isContext || isInnerElement) {
                    return relationships.getComponentsEventuallyReferencingItem(boundObjectPtr)[0] as CompRef
                }
                return boundObjectPtr as CompRef
            }
            const boundRef: BoundObjectRef = isContext
                ? {compRef: resolveCompRef(), contextType: dal.get(boundObjectPtr).contextType}
                : {compRef: resolveCompRef()}
            return boundRef
        }

        const isUnderContext = (boundRef: BoundObjectRef, contextType: string): {underContext: boolean} => {
            const availableContexts = contextsAPI.list(boundRef, {includeAncestors: true})
            return {underContext: availableContexts.some(c => c.contextType === contextType)}
        }

        const isUnderTemplate = (pointer: Pointer, templateComponentId: string): {underTemplate: boolean} => {
            const availableTemplateComponentIds = [
                pointer.id,
                ...components.getAncestors(pointer, true).map(({id}) => id)
            ]
            return {underTemplate: availableTemplateComponentIds.some(id => id === templateComponentId)}
        }

        function validateArgumentsBindings(
            boundRef: BoundObjectRef,
            functionArguments: BindingArgument[],
            errors: ValidatorResult[]
        ): ValidatorResult[] {
            _.forEach(functionArguments, argument => {
                if (argument.type === ArgumentType.ContextBindingItem) {
                    if (!isUnderContext(boundRef, argument.contextProvider).underContext) {
                        errors.push({
                            shouldFail: true,
                            type: 'validateBoundArguments',
                            message: `The context you try to bind to is not attached on the container or any of its ancestors`
                        })
                    }
                    if (
                        argument.contextItem?.bindingItemType === BindingItemType.ContextMethod &&
                        argument.contextItem.callOptions?.arguments?.length > 0
                    ) {
                        validateArgumentsBindings(boundRef, argument.contextItem.callOptions.arguments, errors)
                    }
                } else if (argument.type === ArgumentType.FunctionBinding && argument.arguments?.length > 0) {
                    validateArgumentsBindings(boundRef, argument.arguments, errors)
                } else if (argument.type === ArgumentType.TemplateBindingItem) {
                    if (!isUnderTemplate(boundRef.compRef, argument.templateComponentId).underTemplate) {
                        errors.push({
                            shouldFail: true,
                            type: 'validateBoundArguments',
                            message: `The template you try to bind to is not one of its ancestors`
                        })
                    }
                    if (
                        argument.templateProperty?.bindingItemType === TemplateBindingType.TemplateMethod &&
                        argument.templateProperty.callOptions?.arguments?.length > 0
                    ) {
                        validateArgumentsBindings(boundRef, argument.templateProperty.callOptions.arguments, errors)
                    }
                }
            })

            return errors
        }

        return {
            validatePropertiesAreBoundMaximumOnce: (pointer: Pointer, value: DalValue) => {
                if (value?.type !== 'BindingsList') {
                    return []
                }

                const bindingRefs = value?.data ?? []
                const seenProperties = new Set()
                const errors: ValidatorResult[] = []

                for (const ref of bindingRefs) {
                    const configId = ref.startsWith('#') ? ref.slice(1) : ref
                    const config = dal.get({id: configId, type: 'bindings'})
                    if (!config) {
                        continue
                    }
                    if (config.type === 'FunctionBindingConfig') {
                        if (seenProperties.has(config.propertyName)) {
                            errors.push(generateErrorDuplicatePropertyBinding(config.propertyName))
                        } else {
                            seenProperties.add(config.propertyName)
                        }
                    } else if (config.type === 'ContextBindingConfig' || config.type === 'TemplateBindingConfig') {
                        const propertyNames = Object.keys(config.properties ?? {})
                        for (const prop of propertyNames) {
                            if (seenProperties.has(prop)) {
                                errors.push(generateErrorDuplicatePropertyBinding(config.propertyName))
                            }
                            seenProperties.add(prop)
                        }
                    }
                }

                if (errors.length > 0) {
                    return errors
                }

                return []
            },
            validateBoundPropertiesUnderContext: (pointer: Pointer, value: DalValue) => {
                if (value?.type !== 'ContextBindingConfig') {
                    return []
                }
                const {contextProvider} = value
                const boundRef = getResolvedBoundRefFromConfigPointer(pointer)
                if (!isUnderContext(boundRef, contextProvider).underContext) {
                    return [
                        {
                            shouldFail: true,
                            type: 'validateBoundPropertiesUnderContext',
                            message: `The context you try to bind to is not attached on the container or any of its ancestors`
                        }
                    ]
                }
                return []
            },
            validateBoundPropertiesUnderTemplate: (pointer: Pointer, value: DalValue) => {
                if (value?.type !== 'TemplateBindingConfig') {
                    return []
                }
                const {templateComponentId} = value
                const boundRef = getResolvedBoundRefFromConfigPointer(pointer)
                if (!isUnderTemplate(boundRef.compRef, templateComponentId).underTemplate) {
                    return [
                        {
                            shouldFail: true,
                            type: 'validateBoundPropertiesUnderTemplate',
                            message: `The template you try to bind to does not exist above the component`
                        }
                    ]
                }
                return []
            },
            validateBoundArguments: (pointer: Pointer, value: DalValue) => {
                if (
                    ![
                        BindingsConfigType.ContextBinding,
                        BindingsConfigType.FunctionBinding,
                        BindingsConfigType.TemplateBinding
                    ].includes(value?.type)
                ) {
                    return []
                }
                const errors: ValidatorResult[] = []
                if (value.type === BindingsConfigType.ContextBinding) {
                    _.forEach(value.properties, boundValue => {
                        if (
                            boundValue.bindingItemType === BindingItemType.ContextMethod &&
                            boundValue.callOptions?.arguments &&
                            boundValue.callOptions.arguments.length > 0
                        ) {
                            const boundRef = getResolvedBoundRefFromConfigPointer(pointer)
                            validateArgumentsBindings(boundRef, boundValue.callOptions.arguments, errors)
                        }
                    })
                } else if (value.type === BindingsConfigType.FunctionBinding) {
                    if (value.functionBindingItem?.arguments && value.functionBindingItem.arguments.length > 0) {
                        const boundRef = getResolvedBoundRefFromConfigPointer(pointer)
                        validateArgumentsBindings(boundRef, value.functionBindingItem.arguments, errors)
                    }
                } else if (value.type === BindingsConfigType.TemplateBinding) {
                    _.forEach(value.properties, boundValue => {
                        if (
                            boundValue.bindingItemType === TemplateBindingType.TemplateMethod &&
                            boundValue.callOptions?.arguments &&
                            boundValue.callOptions.arguments.length > 0
                        ) {
                            const boundRef = getResolvedBoundRefFromConfigPointer(pointer)
                            validateArgumentsBindings(boundRef, boundValue.callOptions.arguments, errors)
                        }
                    })
                }
                return errors
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): BindingsExtensionAPI => {
        const {bindings: bindingsAPI} = extensionAPI as BindingsExtensionAPI
        return {
            bindings: {
                bind: bindingsAPI.bind,
                bindAll: bindingsAPI.bindAll,
                unbindProperties: bindingsAPI.unbindProperties,
                getBoundProperties: bindingsAPI.getBoundProperties,
                unbind: bindingsAPI.unbind,
                contexts: {
                    listBoundObjects: bindingsAPI.contexts.listBoundObjects
                },
                functions: {
                    bind: bindingsAPI.functions.bind,
                    listBoundObjects: bindingsAPI.functions.listBoundObjects
                },
                templates: {
                    bind: bindingsAPI.templates.bind,
                    list: bindingsAPI.templates.list
                }
            }
        }
    }

    const initialize = async (extArgs: InitializeExtArgs) => {
        const {extensionAPI, pointers, dal} = extArgs
        const {hooks, dataModel} = extensionAPI as HooksExtensionApi & DataModelExtensionAPI

        const removeContextBindings = (componentPointer: Pointer, contextType: string): void => {
            const bindingsList = dataModel.components.getItem(componentPointer, BINDINGS_NAMESPACE)
            if (!bindingsList?.data?.length) {
                return
            }
            const filteredConfigs = bindingsList.data.filter(
                (config: DalItem) => config.type !== 'ContextBindingConfig' || config.contextProvider !== contextType
            )
            if (_.isEmpty(filteredConfigs)) {
                dataModel.components.removeItem(componentPointer, BINDINGS_NAMESPACE)
            } else if (filteredConfigs.length !== bindingsList.data.length) {
                dataModel.components.addItem(componentPointer, BINDINGS_NAMESPACE, {
                    ...bindingsList,
                    data: filteredConfigs
                })
            }
        }

        const stripHash = (id: string): string => (id.startsWith('#') ? id.slice(1) : id)

        const collectInnerElementsFromMap = (innerElementsMapId: string | undefined, results: Pointer[]): void => {
            if (!innerElementsMapId) {
                return
            }
            const innerElementsMap = dal.get({id: stripHash(innerElementsMapId), type: DATA_TYPES.innerElements})
            if (!innerElementsMap?.innerElements) {
                return
            }
            for (const innerElementId of Object.values(innerElementsMap.innerElements)) {
                const id = stripHash(innerElementId as string)
                const innerElementPointer = {id, type: DATA_TYPES.innerElements}
                results.push(innerElementPointer)
                const innerElement = dal.get(innerElementPointer)
                if (innerElement?.innerElementsQuery) {
                    collectInnerElementsFromMap(innerElement.innerElementsQuery, results)
                }
            }
        }

        const getInnerElementsRecursively = (componentPointer: CompRef): Pointer[] => {
            const component = dal.get(componentPointer)
            if (!component?.innerElementsQuery) {
                return []
            }
            const results: Pointer[] = []
            collectInnerElementsFromMap(component.innerElementsQuery, results)
            return results
        }

        const getDescendantsRecursively = (componentPointer: CompRef): CompRef[] => {
            const children = pointers.structure.getChildren(componentPointer) as CompRef[]
            const descendants: CompRef[] = []
            for (const child of children) {
                descendants.push(child)
                descendants.push(...getDescendantsRecursively(child))
            }
            return descendants
        }

        const cleanupBindingsAfterDetachedContext = (compRef: CompRef, contextType: string): void => {
            const descendants = getDescendantsRecursively(compRef)
            const allComponents = [compRef, ...descendants]
            for (const componentPointer of allComponents) {
                removeContextBindings(componentPointer, contextType)
                const innerElements = getInnerElementsRecursively(componentPointer)
                for (const innerElementPointer of innerElements) {
                    removeContextBindings(innerElementPointer, contextType)
                }
            }
        }

        hooks.registerHook<'context_detach_after', ContextDetachAfterEvent>(
            CONTEXTS_HOOKS.DETACH_AFTER.id,
            ({compRef, contextType}: ContextDetachAfterEvent) => {
                cleanupBindingsAfterDetachedContext(compRef, contextType)
            }
        )
    }

    return {
        dependencies: new Set([
            'contexts',
            'dataModel',
            'structure',
            'hooks',
            'slots',
            'manifest',
            'innerElements',
            'relationships'
        ]),
        name: 'bindings',
        createExtensionAPI,
        createValidator,
        createPublicAPI,
        initialize
    }
}

export {createExtension}
